import { useState, useRef, useEffect, useCallback } from 'react';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',
  text2:'#9890b0',text3:'#55506a',
};

export default function AvatarCrop({ file, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const cropRef = useRef({ x:0, y:0, size:0 });
  const [shape, setShape] = useState('circle');
  const [saving, setSaving] = useState(false);
  const [loadedImg, setLoadedImg] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setLoadedImg(img);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = loadedImg;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    const { x, y, size } = cropRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(x + size/2, y + size/2, size/2, 0, Math.PI*2);
    } else {
      ctx.rect(x, y, size, size);
    }
    ctx.clip();
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.strokeStyle = T.gold;
    ctx.lineWidth = 2;
    if (shape === 'circle') {
      ctx.beginPath();
      ctx.arc(x + size/2, y + size/2, size/2, 0, Math.PI*2);
      ctx.stroke();
    } else {
      ctx.strokeRect(x, y, size, size);
    }
    [[x,y],[x+size,y],[x,y+size],[x+size,y+size]].forEach(([hx,hy]) => {
      ctx.fillStyle = T.gold;
      ctx.beginPath();
      ctx.arc(hx, hy, 6, 0, Math.PI*2);
      ctx.fill();
    });
  }, [loadedImg, shape]);

  useEffect(() => {
    if (!loadedImg || !canvasRef.current) return;
    const MAX = 380;
    const scale = Math.min(MAX/loadedImg.naturalWidth, MAX/loadedImg.naturalHeight, 1);
    const W = Math.round(loadedImg.naturalWidth * scale);
    const H = Math.round(loadedImg.naturalHeight * scale);
    canvasRef.current.width = W;
    canvasRef.current.height = H;
    const size = Math.round(Math.min(W, H) * 0.8);
    cropRef.current = { x: Math.round((W-size)/2), y: Math.round((H-size)/2), size };
    draw();
  }, [loadedImg, draw]);

  useEffect(() => { draw(); }, [shape, draw]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const sx = canvas.width / r.width;
    const sy = canvas.height / r.height;
    const src = e.touches ? e.touches[0] : e;
    return { x:(src.clientX-r.left)*sx, y:(src.clientY-r.top)*sy };
  }, []);

  const hitHandle = useCallback((px, py) => {
    const { x, y, size } = cropRef.current;
    const handles = [
      {name:'tl',hx:x,hy:y},{name:'tr',hx:x+size,hy:y},
      {name:'bl',hx:x,hy:y+size},{name:'br',hx:x+size,hy:y+size},
    ];
    return handles.find(h => Math.hypot(px-h.hx, py-h.hy) < 18)?.name || null;
  }, []);

  // Wire pointer events directly to DOM to avoid React synthetic event issues
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !loadedImg) return;

    const onDown = (e) => {
      e.preventDefault();
      const { x:px, y:py } = getPos(e);
      const { x, y, size } = cropRef.current;
      const handle = hitHandle(px, py);
      if (handle) {
        drag.current = { type:'resize', handle, startX:px, startY:py, orig:{...cropRef.current} };
      } else if (px>=x && px<=x+size && py>=y && py<=y+size) {
        drag.current = { type:'move', startX:px, startY:py, orig:{...cropRef.current} };
      }
    };

    const onMove = (e) => {
      if (!drag.current) return;
      e.preventDefault();
      const { x:px, y:py } = getPos(e);
      const { startX, startY, orig, type, handle } = drag.current;
      const dx = px-startX, dy = py-startY;
      const W = canvas.width, H = canvas.height;
      let { x, y, size } = orig;
      if (type === 'move') {
        x = Math.max(0, Math.min(W-size, orig.x+dx));
        y = Math.max(0, Math.min(H-size, orig.y+dy));
      } else {
        const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
        if (handle==='br') size=Math.max(40,Math.min(W-orig.x,H-orig.y,orig.size+delta));
        else if (handle==='tl') { size=Math.max(40,orig.size-delta); x=orig.x+orig.size-size; y=orig.y+orig.size-size; }
        else if (handle==='tr') { size=Math.max(40,orig.size+delta); y=orig.y+orig.size-size; }
        else if (handle==='bl') { size=Math.max(40,orig.size+delta); x=orig.x+orig.size-size; }
        x=Math.max(0,x); y=Math.max(0,y);
        size=Math.min(size,W-x,H-y);
      }
      cropRef.current = { x, y, size };
      draw();
    };

    const onUp = () => { drag.current = null; };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    canvas.addEventListener('touchend', onUp);

    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onUp);
    };
  }, [loadedImg, draw, getPos, hitHandle]);

  const handleSave = async () => {
    setSaving(true);
    const img = loadedImg;
    const canvas = canvasRef.current;
    const { x, y, size } = cropRef.current;
    const sx = img.naturalWidth / canvas.width;
    const sy = img.naturalHeight / canvas.height;
    const out = document.createElement('canvas');
    out.width = 400; out.height = 400;
    const ctx = out.getContext('2d');
    if (shape === 'circle') {
      ctx.beginPath(); ctx.arc(200,200,200,0,Math.PI*2); ctx.clip();
    }
    ctx.drawImage(img, x*sx, y*sy, size*sx, size*sy, 0, 0, 400, 400);
    out.toBlob(async (blob) => {
      if (blob) await onSave(new File([blob], 'avatar.jpg', { type:'image/jpeg' }));
      setSaving(false);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.9)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',fontFamily:"'DM Sans','Jost',sans-serif"}}>
      <div style={{background:T.s1,border:`1px solid ${T.border2}`,borderRadius:16,width:'100%',maxWidth:480,overflow:'hidden'}}>
        <div style={{padding:'1rem 1.25rem',borderBottom:`1px solid ${T.border}`,background:T.s2,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontWeight:700,fontSize:15,color:T.cream}}>Crop Profile Photo</div>
          <button onClick={onCancel} style={{background:'none',border:'none',color:T.text2,fontSize:20,cursor:'pointer'}}>✕</button>
        </div>
        <div style={{display:'flex',gap:8,padding:'0.75rem 1.25rem',background:T.s2,borderBottom:`1px solid ${T.border}`}}>
          {['circle','square'].map(s => (
            <button key={s} onClick={() => setShape(s)} style={{flex:1,padding:'6px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',background:shape===s?`${T.gold}20`:T.s3,border:`1px solid ${shape===s?T.gold:T.border2}`,color:shape===s?T.gold:T.text2}}>
              {s === 'circle' ? '⬤ Circle' : '■ Square'}
            </button>
          ))}
        </div>
        <div style={{padding:'1rem',display:'flex',justifyContent:'center',alignItems:'center',background:T.bg,minHeight:200}}>
          {!loadedImg
            ? <div style={{color:T.text3,fontSize:13}}>Loading image...</div>
            : <canvas
                ref={canvasRef}
                style={{
                  maxWidth:'100%',
                  maxHeight:'45vh',
                  cursor:'crosshair',
                  borderRadius:8,
                  display:'block',
                  touchAction:'none',
                  userSelect:'none',
                  WebkitUserSelect:'none',
                }}
              />
          }
        </div>
        <div style={{padding:'0.5rem 1.25rem',fontSize:11,color:T.text3,background:T.s2,borderTop:`1px solid ${T.border}`}}>
          Drag the crop area to reposition · Pull gold corners to resize
        </div>
        <div style={{display:'flex',gap:10,padding:'1rem 1.25rem',background:T.s2,borderTop:`1px solid ${T.border}`}}>
          <button onClick={onCancel} style={{flex:1,padding:'0.7rem',borderRadius:8,border:`1px solid ${T.border2}`,background:'none',color:T.text2,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
          <button onClick={handleSave} disabled={saving||!loadedImg} style={{flex:2,padding:'0.7rem',borderRadius:8,border:'none',background:`linear-gradient(135deg,${T.gold},${T.gold2})`,color:T.bg,fontSize:13,fontWeight:700,cursor:saving?'default':'pointer',fontFamily:'inherit',opacity:saving||!loadedImg?0.6:1}}>
            {saving ? 'Saving...' : 'Save Photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
